// idbStorage.js — tiny promise-based wrapper around a single IndexedDB
// object store, used to replace localStorage for the Ungate Crawler data.

const DB_NAME = "MSPUngateCrawlerDB";
const DB_VERSION = 1;
const STORE_NAME = "kv";

let dbPromise = null;

function openDB() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    return dbPromise;
}

export async function idbGet(key) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const req = tx.objectStore(STORE_NAME).get(key);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

export async function idbSet(key, value) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        tx.objectStore(STORE_NAME).put(value, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

export async function idbDeleteKey(key) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        tx.objectStore(STORE_NAME).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

// One-time migration: for every key in `keys`, if a value still exists in
// localStorage, copy it into IndexedDB (unless IndexedDB already has a value
// for that key) and then remove it from localStorage. Safe to call on every
// mount — it's a no-op once localStorage is empty for these keys.
export async function migrateKeysFromLocalStorage(keys) {
    for (const key of keys) {
        const raw = localStorage.getItem(key);
        if (raw === null) continue; // nothing to migrate for this key
        try {
            const existing = await idbGet(key);
            if (existing === undefined) {
                await idbSet(key, raw);
            }
        } catch (e) {
            console.error(`IndexedDB migration failed for "${key}"`, e);
            continue; // don't delete from localStorage if migration failed
        }
        localStorage.removeItem(key);
    }
}

// Async replacements for the old loadSet/saveSet localStorage helpers.
export async function idbLoadSet(key) {
    try {
        const raw = await idbGet(key);
        return new Set(raw ? JSON.parse(raw) : []);
    } catch {
        return new Set();
    }
}
export async function idbSaveSet(key, s) {
    try {
        await idbSet(key, JSON.stringify([...s]));
    } catch { /* best-effort */ }
}

// Async replacements for the historical-leads object blob.
export async function idbLoadObject(key) {
    try {
        const raw = await idbGet(key);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
}
export async function idbSaveObject(key, obj) {
    try {
        await idbSet(key, JSON.stringify(obj));
    } catch { /* best-effort */ }
}